<template>
    <div class="escritorio-clinico-digital">
        <!-- Barra de menús del expediente -->
        <MenuBar
class="barra-global" :left-panel-collapsed="isLeftPanelCollapsed"
            :right-panel-collapsed="isRightPanelCollapsed" @toggle-panel="togglePanel" @filtrar="aplicarFiltro"
            @filtrar-avanzado="aplicarFiltroAvanzado" />

        <!-- Contenedor de paneles -->
        <div class="paneles-container" :class="layoutClasses">
            <ActionsPanel class="panel-izquierdo" :patient-id="patientId" @accion="manejarAccion" />

            <PatientTimeline
class="panel-central" :patient-id="patientId" :filtro-actual="filtroActual"
                :filtro-avanzado="filtroAvanzado" :nota-seleccionada-id="detalleSeleccionado?.id"
                @seleccionar-nota="seleccionarNota" />

            <DetailsPanel
class="panel-derecho" :item-seleccionado="detalleSeleccionado"
                :is-expanded="isDetailPanelExpanded" @cerrar="cerrarDetalle" @toggle-expand="toggleDetailExpansion" />
        </div>
    </div>
</template>

<script setup>
import { defineProps, onMounted, watch } from 'vue';
import ActionsPanel from './Navigation/ActionsPanel.vue';
import PatientTimeline from './Timeline/PatientTimeline.vue';
import DetailsPanel from './Details/DetailsPanel.vue';
import MenuBar from './Navigation/MenuBar.vue';
import { useMedicalWorkspace } from '@/composables/useMedicalWorkspace';

const props = defineProps({
    patientId: {
        type: [String, Number],
        required: true
    }
});

// Usar el composable centralizado para manejar todo el estado
/* eslint-disable no-unused-vars */
const {
    // Estado
    isLeftPanelCollapsed,
    isRightPanelCollapsed,
    isDetailPanelExpanded,
    detalleSeleccionado,
    filtroActual,
    filtroAvanzado,

    // Computed
    layoutClasses,
    esPacienteNuevo,
    pacienteTieneContenido,

    // Métodos
    togglePanel,
    seleccionarNota,
    cerrarDetalle,
    toggleDetailExpansion,
    aplicarFiltro,
    aplicarFiltroAvanzado,
    activarModoAccion,
    setPatientId,
    cargarDatosPaciente,
    cargarPacienteDesdeStore,
    inicializarConVistaGuardada,
} = useMedicalWorkspace();
/* eslint-enable no-unused-vars */

// Inicializar el ID del paciente cuando el componente se monta
onMounted(() => {
    // 🔄 Usar la nueva función que restaura la vista guardada
    inicializarConVistaGuardada(props.patientId);

    const cargado = cargarPacienteDesdeStore();

    if (!cargado) {
        const datosPacienteMinimo = {
            id: props.patientId || null,
            first_name: '',
            last_name: '',
            gender: '',
            birthdate: '',
            email: '',
            whatsapp: '',
            profile: {},
            historiaClinica: {
                antecedentesPersonales: [],
                antecedentesFamiliares: [],
                alergias: [],
                medicamentosActuales: [],
                cirugiasPrevias: [],
                enfermedadesCronicas: []
            },
            notasMedicas: [],
            recetas: [],
            documentos: [],
            signosVitales: []
        };

        cargarDatosPaciente(datosPacienteMinimo);
    }
});

// 🔄 Watcher para detectar cambios de paciente y reiniciar el estado
watch(() => props.patientId, (newId) => {
    if (newId) {
        inicializarConVistaGuardada(newId);
        cargarPacienteDesdeStore();
    }
});

// Manejar acciones desde BlocAcciones
const manejarAccion = (accion) => {
    if (accion.tipo === 'filtrar') {
        aplicarFiltro(accion.payload);
    }
    // Lógica para otras acciones como 'nueva-nota', 'nueva-receta', etc.
};

</script>

<style scoped>
.escritorio-clinico-digital {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 40px);
    /* Aumentado para que los formularios se vean más completos */
    min-height: 700px;
    /* Altura mínima aumentada para mejores formularios */
    padding: 0.5rem;
    /* Reducido padding para más espacio */
    background-color: #f8f9fe;
    gap: 0.75rem;
    /* Reducido gap para más espacio vertical */
}

.barra-global {
    flex-shrink: 0;
    z-index: 10;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.paneles-container {
    display: grid;
    grid-template-columns: 300px 1fr 400px;
    grid-template-rows: 1fr;
    gap: 0.5rem;
    /* Reducido para más espacio vertical */
    flex-grow: 1;
    min-height: 0;
    /* Importante: permite que los hijos respeten el overflow */
    overflow: hidden;
    transition: grid-template-columns 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-izquierdo,
.panel-central,
.panel-derecho {
    min-height: 0;
    /* Importante: permite que funcione el overflow */
    height: 100%;
    /* Asegura que ocupe todo el espacio disponible */
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    background: white;
    /* Asegura visibilidad */
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    /* Para debugging */
}

/* Debugging: asegurar que los paneles sean visibles */
.panel-izquierdo::before,
.panel-central::before,
.panel-derecho::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    opacity: 0.3;
    pointer-events: none;
}

/* Permitir scroll en panel izquierdo */
.panel-izquierdo {
    overflow-y: auto;
    overflow-x: hidden;
}

.panel-central {
    overflow-y: auto;
    overflow-x: hidden;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-derecho {
    overflow-y: auto;
    overflow-x: hidden;
}

/* --- TRANSICIONES SUAVES PARA GRID --- */
.escritorio-clinico-digital {
    transition: grid-template-columns 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ========================================
   MODO NORMAL: 3 Columnas visibles
   Panel Izquierdo (300px) + Timeline (expandido) + Panel Derecho (oculto)
   ======================================== */
.paneles-container.modo-normal {
    grid-template-columns: 300px 1fr 0px;
}

/* IMPORTANTE: Modo normal con panel izquierdo colapsado */
.paneles-container.modo-normal.left-collapsed {
    grid-template-columns: 0px 1fr 0px !important;
}

.paneles-container.modo-normal .panel-derecho {
    opacity: 0;
    pointer-events: none;
    transform: translateX(20px);
}

.paneles-container.modo-normal .panel-izquierdo {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 300px;
    /* Asegurar ancho mínimo cuando visible */
}

.paneles-container.modo-normal .panel-central {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* IMPORTANTE: Ocultar panel izquierdo cuando está colapsado en modo normal */
.paneles-container.modo-normal.left-collapsed .panel-izquierdo {
    opacity: 0 !important;
    pointer-events: none !important;
    transform: translateX(-20px) !important;
    min-width: 0 !important;
    /* Ancho 0 cuando colapsado */
}

/* ========================================
   MODO DETALLE: Timeline + Detalle (sin panel izquierdo)
   Timeline (1 columna - 33%) + Detalle (2 columnas - 67%)
   ======================================== */
.paneles-container.modo-detalle-activo {
    grid-template-columns: 0px minmax(300px, 1fr) minmax(400px, 2fr);
}

.paneles-container.modo-detalle-activo .panel-izquierdo {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-20px);
}

.paneles-container.modo-detalle-activo .panel-central,
.paneles-container.modo-detalle-activo .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* ========================================
   MODO DETALLE CON ACCIONES: Las 3 columnas visibles
   Acciones (250px) + Timeline (1fr) + Detalle (1.5fr)
   ======================================== */
.paneles-container.modo-detalle-con-acciones {
    grid-template-columns: 250px minmax(300px, 1fr) minmax(400px, 1.5fr);
}

.paneles-container.modo-detalle-con-acciones .panel-izquierdo,
.paneles-container.modo-detalle-con-acciones .panel-central,
.paneles-container.modo-detalle-con-acciones .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* ========================================
   MODO FORMULARIO: Timeline + Formulario (sin panel izquierdo)
   Timeline (40%) + Formulario (60%) para más espacio al formulario
   ======================================== */
.paneles-container.modo-formulario-activo {
    grid-template-columns: 0px minmax(350px, 40fr) minmax(450px, 60fr);
}

.paneles-container.modo-formulario-activo .panel-izquierdo {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-20px);
}

.paneles-container.modo-formulario-activo .panel-central,
.paneles-container.modo-formulario-activo .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* ========================================
   MODO FORMULARIO CON ACCIONES: Las 3 columnas visibles con formulario
   Acciones (250px) + Timeline (30%) + Formulario (60%)
   ======================================== */
.paneles-container.modo-formulario-con-acciones {
    grid-template-columns: 250px minmax(250px, 30fr) minmax(400px, 60fr);
}

.paneles-container.modo-formulario-con-acciones .panel-izquierdo,
.paneles-container.modo-formulario-con-acciones .panel-central,
.paneles-container.modo-formulario-con-acciones .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* ========================================
   MODO EXPEDIENTE COMPLETO: BlocAcciones + Historia Clínica
   Panel Izquierdo (300px) + Historia Clínica (resto)
   ======================================== */
.paneles-container.modo-expediente-completo {
    grid-template-columns: 300px 1fr 0px;
}

.paneles-container.modo-expediente-completo .panel-izquierdo,
.paneles-container.modo-expediente-completo .panel-central {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

.paneles-container.modo-expediente-completo .panel-derecho {
    opacity: 0;
    pointer-events: none;
    transform: translateX(20px);
}

.paneles-container.modo-expediente-completo.left-collapsed {
    grid-template-columns: 0px 1fr 0px;
}

.paneles-container.modo-expediente-completo.left-collapsed .panel-izquierdo {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-20px);
    min-width: 0;
}

/* ========================================
   MODO PERFIL CON NAVEGACION: BlocAcciones + Timeline + Perfil
   Permite navegacion fluida mientras se ve el perfil del paciente
   ======================================== */
.paneles-container.modo-perfil-con-navegacion {
    grid-template-columns: 300px 0px 1fr;
}

.paneles-container.modo-perfil-con-navegacion .panel-izquierdo,
.paneles-container.modo-perfil-con-navegacion .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

.paneles-container.modo-perfil-con-navegacion .panel-central {
    opacity: 0;
    pointer-events: none;
    width: 0;
    overflow: hidden;
}

/* ========================================
   MODO HISTORIA EXPANDIDA: Solo Historia Clinica (Paciente Nuevo)
   Historia Clinica al 100% cuando no hay notas/recetas/documentos
   ======================================== */
.paneles-container.modo-historia-expandida {
    grid-template-columns: 0px 1fr 0px !important;
}

.paneles-container.modo-historia-expandida .panel-izquierdo,
.paneles-container.modo-historia-expandida .panel-derecho {
    opacity: 0 !important;
    pointer-events: none !important;
    transform: translateX(-20px);
}

.paneles-container.modo-historia-expandida .panel-central {
    opacity: 1 !important;
    pointer-events: all !important;
    transform: translateX(0);
}

/* ========================================
   MODO DETALLE EXPANDIDO: Solo panel derecho
   Detalle al 100% cuando se hace clic en expandir
   PERO permite mostrar BlocAcciones si el usuario lo activa
   ======================================== */
/* Cuando el detalle está expandido Y el panel izquierdo está colapsado */
.paneles-container.detail-expanded.left-collapsed {
    grid-template-columns: 0px 0px 1fr !important;
}

/* Cuando el detalle está expandido PERO el panel izquierdo está visible */
.paneles-container.detail-expanded:not(.left-collapsed) {
    grid-template-columns: 300px 0px 1fr !important;
}

.paneles-container.detail-expanded .panel-central {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-20px);
}

.paneles-container.detail-expanded .panel-derecho {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* Panel izquierdo visible en detalle expandido */
.paneles-container.detail-expanded:not(.left-collapsed) .panel-izquierdo {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}

/* Panel izquierdo oculto en detalle expandido */
.paneles-container.detail-expanded.left-collapsed .panel-izquierdo {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-20px);
}

.paneles-container.modo-expediente-completo .panel-central {
    opacity: 1;
    pointer-events: all;
    transform: translateX(0);
}


/* ========================================
   TRANSICIONES Y ANIMACIONES SUAVES
   ======================================== */
.panel-izquierdo> :not(.handle),
.panel-derecho> :not(.handle) {
    transition: opacity 0.4s ease-in-out;
}

/* Ocultar contenido cuando panel está colapsado */
.modo-detalle-activo .panel-izquierdo> :not(.handle),
.modo-formulario-activo .panel-izquierdo> :not(.handle),
.modo-expediente-completo .panel-derecho> :not(.handle) {
    opacity: 0;
}

/* Mostrar contenido cuando panel está visible */
.modo-normal .panel-izquierdo> :not(.handle),
.modo-normal .panel-central> :not(.handle),
.modo-detalle-activo .panel-central> :not(.handle),
.modo-detalle-activo .panel-derecho> :not(.handle),
.modo-formulario-activo .panel-central> :not(.handle),
.modo-formulario-activo .panel-derecho> :not(.handle),
.modo-expediente-completo .panel-izquierdo> :not(.handle),
.modo-expediente-completo .panel-central> :not(.handle) {
    opacity: 1;
}

.contenedor-central {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
}

.linea-historia-panel {
    flex-grow: 1;
    overflow-y: auto;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Mejora del scroll en modo expediente completo */
.modo-expediente-completo .panel-central {
    overflow-y: auto;
    scroll-behavior: smooth;
}
</style>
